// Twelve-influence skinning for the NODE path.
//
// `skinInfluences.js` extends three's four-influence skinning by patching the
// shared `ShaderChunk` table behind `AOS_SKIN_SETS_2/3` defines. There is no such
// table under `WebGPURenderer` — skinning is a node graph — so this is the same
// arithmetic expressed as TSL.
//
// WHY IT MATTERS, restated because it is easy to read as a micro-optimisation:
// an Unreal MetaHuman export skins the face to TWELVE joints (three glTF sets,
// 6.8 per vertex on average). GLTFLoader binds JOINTS_0/WEIGHTS_0 and drops the
// rest, and the first four carry 89% of a vertex on average but only 39% at
// worst — so a four-influence path renormalised to 1 breaks a posed cheek into
// shards exactly where the face joints overlap.
//
// ── The seam ───────────────────────────────────────────────────────────────
//
// `NodeMaterial.setupPosition` calls the module-level `skinning(object)` for any
// `isSkinnedMesh`, and neither that call nor the function it calls can be
// intercepted per material. `positionNode` is no use either: it is applied at the
// END of `setupPosition`, by which point `positionLocal` already holds the
// four-influence result, and the morphed-but-unskinned position it was computed
// from is gone — so a correction cannot be derived from it.
//
// So this REPLACES `setupPosition` on the instance. The replacement is a faithful
// copy of three's (morph, skin, the `positionNode` override) built entirely from
// public TSL — `morphReference`, `subBuild`, `referenceBuffer`, `OnObjectUpdate`
// are all exported — with the skinning call swapped. The one piece deliberately
// NOT reproduced is `displacementMap`, which a graph material never sets; if that
// ever changes this must grow the branch, so it throws rather than ignoring it.
//
// HOW A THREE UPGRADE IS CAUGHT. Not by sniffing three's source, which minifies
// away in a production build. `extendedSkinning` at ONE set must be numerically
// identical to three's own `skinning`, and the pixel harness asserts exactly
// that: the same rig rendered through a stock node material and through this one
// must differ by zero. If three changes its skinning maths, that comparison fails
// — which is the same shape of guard `installExtendedSkinning` gets from its
// anchor check, but immune to minification.
//
// ── The bone matrices ──────────────────────────────────────────────────────
//
// three picks between a uniform buffer and a bone TEXTURE by the adapter's
// uniform-buffer limit, and the texture half goes through a private helper. Only
// the buffer half is reproduced here; a skeleton too large for it REPORTS a
// downgrade and keeps three's four-influence skinning rather than rendering an
// un-skinned face or silently dropping the extra sets. At the WebGPU default of
// 65536 bytes that boundary is 1024 bones, and a MetaHuman face is 870 — so the
// fallback is a real limit rather than the usual case.

import {
  attribute, reference, referenceBuffer, mat3, subBuild, skinning,
  positionLocal, normalLocal, tangentLocal, morphReference, OnObjectUpdate,
} from 'three/tsl'
import { MAX_SKIN_SETS, skinIndexAttr, skinWeightAttr } from './skinInfluences.js'

const BYTES_PER_BONE = 16 * 4
const COMPONENTS = ['x', 'y', 'z', 'w']

/** How many bones fit the uniform-buffer path on this device. Exported so the
 *  caller can report the reason rather than "skinning is degraded". */
export function maxUniformBones(builder) {
  return Math.floor(builder.getUniformBufferLimit() / BYTES_PER_BONE)
}

/**
 * The extended skin, assigning `positionLocal` / `normalLocal` / `tangentLocal`
 * exactly as three's `skinning` does — same order, same composition — over `sets`
 * attribute pairs instead of one.
 *
 * A plain function rather than an `Fn`, because it only writes into the stack
 * `setupPosition` has already opened — the same way `morphReference` and three's
 * own displacement branch are called bare from there.
 */
function buildExtendedSkinning(builder, skinnedMesh, sets) {
  const skeleton = skinnedMesh.skeleton
  const bones = referenceBuffer('skeleton.boneMatrices', 'mat4', skeleton.bones.length)
  const bindMatrix = reference('bindMatrix', 'mat4')
  const bindMatrixInverse = reference('bindMatrixInverse', 'mat4')

  // three's `skinning` registers this, and it is what actually refreshes the
  // bone matrices each frame. Replacing the call without replacing the hook
  // leaves a rig posed at whatever its matrices held on the first frame — a
  // character frozen mid-pose with no error anywhere.
  OnObjectUpdate(({ object }) => { object.skeleton.update() })

  const pairs = []
  for (let n = 0; n < Math.min(sets, MAX_SKIN_SETS); n++) {
    pairs.push({
      index: attribute(skinIndexAttr(n), 'uvec4'),
      weight: attribute(skinWeightAttr(n), 'vec4'),
    })
  }

  // POSITION — the bind matrix maps into skinning space, every influence sums
  // there, and the inverse maps back. Identical to three's `getSkinnedPosition`
  // with the sum running over all sets.
  const skinVertex = bindMatrix.mul(positionLocal)
  let skinned = null
  for (const p of pairs) {
    for (const c of COMPONENTS) {
      const term = bones.element(p.index[c]).mul(p.weight[c]).mul(skinVertex)
      skinned = skinned === null ? term : skinned.add(term)
    }
  }

  // NORMAL / TANGENT — the weighted sum of the bone matrices themselves, then
  // conjugated by the bind pair. Both are read BEFORE either is assigned: three
  // computes the pair and assigns after, and assigning the normal first would
  // feed an already-skinned normal into the tangent.
  let skinMatrix = null
  for (const p of pairs) {
    for (const c of COMPONENTS) {
      const term = p.weight[c].mul(bones.element(p.index[c]))
      skinMatrix = skinMatrix === null ? term : skinMatrix.add(term)
    }
  }
  skinMatrix = bindMatrixInverse.mul(skinMatrix).mul(bindMatrix)
  const skinMatrix3 = mat3(skinMatrix)

  const hasNormal = builder.hasGeometryAttribute('normal')
  const hasTangent = builder.hasGeometryAttribute('tangent')
  const nextNormal = hasNormal ? skinMatrix3.mul(normalLocal) : null
  const nextTangent = hasNormal && hasTangent ? skinMatrix3.mul(tangentLocal) : null

  positionLocal.assign(bindMatrixInverse.mul(skinned).xyz)
  if (nextNormal) normalLocal.assign(nextNormal)
  if (nextTangent) tangentLocal.assign(nextTangent)
}

/**
 * Replace a node material's `setupPosition` so a skinned mesh under it uses every
 * influence the file carries.
 *
 * `sets` is the geometry's own count (1..3), the same number
 * `enableExtendedSkinning` turns into defines on the GLSL path. At 1 this is
 * removed rather than installed as a no-op: stock three is the reference, so a
 * one-set rig must go through three's own code and not a reimplementation of it.
 *
 * @returns {boolean} whether the material changed.
 */
export function installNodeSkinning(material, sets, { onDowngrade = null } = {}) {
  if (!material || !material.isNodeMaterial) return false
  const want = sets > 1
  const had = !!material.userData?.extendedNodeSkinning

  if (!want) {
    if (!had) return false
    delete material.setupPosition          // back to the prototype's
    material.userData.extendedNodeSkinning = 0
    material.needsUpdate = true
    return true
  }
  if (material.userData?.extendedNodeSkinning === sets) return false

  material.setupPosition = function setupPositionExtended(builder) {
    const { object, geometry } = builder

    if (geometry.morphAttributes.position || geometry.morphAttributes.normal
      || geometry.morphAttributes.color) {
      morphReference(object)
    }

    if (object.isSkinnedMesh === true) {
      // The limit is a property of the DEVICE, so it can only be asked here —
      // `builder` is the first place the adapter's uniform-buffer size is known.
      const fits = object.skeleton.bones.length <= maxUniformBones(builder)
      if (fits) {
        buildExtendedSkinning(builder, object, sets)
      } else {
        skinning(object)
        onDowngrade?.(`skeleton has ${object.skeleton.bones.length} bones, past the `
          + `${maxUniformBones(builder)} this device fits in a uniform buffer — `
          + 'four-influence skinning only')
      }
    }

    if (this.displacementMap) {
      // Reproducing three's displacement branch would be dead code on every
      // material this is installed on, and dead code in a copy of an upstream
      // routine is how a copy silently stops matching. If a graph material ever
      // grows one, this is the line that says to add it.
      throw new Error('[skinInfluencesTSL] displacementMap is not reproduced in the extended setupPosition')
    }

    if (this.positionNode !== null) {
      positionLocal.assign(subBuild(this.positionNode, 'POSITION', 'vec3'))
    }
  }

  material.userData = material.userData || {}
  material.userData.extendedNodeSkinning = sets
  material.needsUpdate = true
  return true
}
